import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Language } from '@/types/multilingual'

// 博客列表返回类型
export interface MultilingualBlogItem {
  id: string
  slug: string
  author_id: string | null
  image: string | null
  tags: string[]
  is_published: boolean
  published_at: string | null
  sort_order: number
  created_at: string
  updated_at: string
  // 翻译字段
  title: string
  description: string
  content: string
}

// 获取多语言博客列表
export function useMultilingualBlogs(language: Language = 'zh', published?: boolean) {
  return useQuery({
    queryKey: ['multilingual-blogs', language, published],
    queryFn: async () => {
      // 获取博客
      let blogQuery = supabase
        .from('multilingual_blogs')
        .select('*')
        .order('sort_order', { ascending: true })
      
      if (published !== undefined) {
        blogQuery = blogQuery.eq('is_published', published)
      }
      
      const { data: blogs, error: blogError } = await blogQuery
      
      if (blogError) throw blogError
      if (!blogs || blogs.length === 0) return []
      
      // 获取对应语言的翻译
      const blogIds = blogs.map(b => b.id)
      const { data: translations, error: transError } = await supabase
        .from('blog_translations')
        .select('*')
        .in('blog_id', blogIds)
        .eq('language', language)
      
      if (transError) throw transError
      
      // 创建翻译映射
      const translationMap = new Map(
        translations?.map(t => [t.blog_id, t]) || []
      )
      
      // 合并数据
      return blogs.map(blog => {
        const trans = translationMap.get(blog.id)
        return {
          ...blog,
          tags: Array.isArray(blog.tags) ? blog.tags : [],
          title: trans?.title || blog.slug,
          description: trans?.description || '',
          content: trans?.content || ''
        } as MultilingualBlogItem
      })
    }
  })
}

// 获取单个多语言博客
export function useMultilingualBlog(slug: string, language: Language = 'zh') {
  return useQuery({
    queryKey: ['multilingual-blog', slug, language],
    queryFn: async () => {
      // 获取博客
      const { data: blog, error: blogError } = await supabase
        .from('multilingual_blogs')
        .select('*')
        .eq('slug', slug)
        .maybeSingle()
      
      if (blogError) throw blogError
      if (!blog) return null
      
      // 获取翻译
      const { data: trans, error: transError } = await supabase
        .from('blog_translations')
        .select('*')
        .eq('blog_id', blog.id)
        .eq('language', language)
        .maybeSingle()
      
      if (transError) throw transError
      
      return {
        ...blog,
        tags: Array.isArray(blog.tags) ? blog.tags : [],
        title: trans?.title || blog.slug,
        description: trans?.description || '',
        content: trans?.content || ''
      } as MultilingualBlogItem
    },
    enabled: !!slug
  })
}

// 删除博客
export function useDeleteMultilingualBlog() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (id: string) => {
      // 先删除翻译
      const { error: transError } = await supabase
        .from('blog_translations')
        .delete()
        .eq('blog_id', id)
      
      if (transError) throw transError
      
      // 再删除博客
      const { error } = await supabase
        .from('multilingual_blogs')
        .delete()
        .eq('id', id)
      
      if (error) throw error
      
      return { id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['multilingual-blogs'] })
    }
  })
}

// 创建博客
export function useCreateMultilingualBlog() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      blog: {
        slug: string
        image?: string
        tags?: string[]
        is_published?: boolean
        published_at?: string
        sort_order?: number
      }
      translations: {
        zh: { title: string; description: string; content: string }
        en: { title: string; description: string; content: string }
      }
    }) => {
      // 插入博客
      const { data: newBlog, error: blogError } = await supabase
        .from('multilingual_blogs')
        .insert({
          slug: data.blog.slug,
          image: data.blog.image || null,
          tags: data.blog.tags || [],
          is_published: data.blog.is_published || false,
          published_at: data.blog.published_at || null,
          sort_order: data.blog.sort_order || 0
        })
        .select('id')
        .single()
      
      if (blogError) throw blogError
      
      // 插入翻译
      const translations = [
        { blog_id: newBlog.id, language: 'zh', ...data.translations.zh },
        { blog_id: newBlog.id, language: 'en', ...data.translations.en }
      ]
      
      const { error: transError } = await supabase
        .from('blog_translations')
        .insert(translations)
      
      if (transError) throw transError
      
      return { id: newBlog.id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['multilingual-blogs'] })
    }
  })
}

// 更新博客
export function useUpdateMultilingualBlog() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: {
      id: string
      blog?: Partial<{
        slug: string
        image: string | null
        tags: string[]
        is_published: boolean
        published_at: string | null
        sort_order: number
      }>
      translations?: {
        zh?: { title?: string; description?: string; content?: string }
        en?: { title?: string; description?: string; content?: string }
      }
    }) => {
      // 更新博客基本信息
      if (data.blog) {
        const { error: blogError } = await supabase
          .from('multilingual_blogs')
          .update(data.blog)
          .eq('id', data.id)
        
        if (blogError) throw blogError
      }
      
      // 更新翻译
      if (data.translations) {
        for (const [lang, trans] of Object.entries(data.translations)) {
          if (trans) {
            const { error: transError } = await supabase
              .from('blog_translations')
              .update(trans)
              .eq('blog_id', data.id)
              .eq('language', lang)
            
            if (transError) throw transError
          }
        }
      }
      
      return { id: data.id }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['multilingual-blogs'] })
    }
  })
}
